function escapeHtml(str) {
    return String(str || "").replace(/[&<>"']/g, function (m) {
        return ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#039;"
        })[m];
    });
}

function compactObject(obj) {
    const out = {};
    if (!obj) return out;
    Object.keys(obj).forEach((k) => {
        const v = obj[k];
        if (v !== null && typeof v !== "undefined" && v !== "") out[k] = v;
    });
    return out;
}

function castValue(raw, type) {
    if (type === "integer") {
        const n = parseInt(raw, 10);
        return isNaN(n) ? null : n;
    }
    if (type === "boolean") return raw === "true";
    if (type === "array") {
        if (Array.isArray(raw)) return raw;
        if (typeof raw === "string" && raw.length > 0) {
            // handle multi-select for HTML select[multiple]
            return Array.from(raw.split(",").map(x => x.trim())).filter(x => x.length > 0);
        }
        return [];
    }
    return raw;
}

function findCondition(conditions, key) {
    return conditions.find((c) => c.key === key);
}

// ---------- Tree State helpers ----------
function isGroup(node) {
    return node && (node.op === "AND" || node.op === "OR");
}

function isNot(node) {
    return node && node.op === "NOT";
}

function ensureRoot(rule) {
    if (rule && (rule.op === "AND" || rule.op === "OR") && Array.isArray(rule.args)) return rule;
    return { op: "AND", args: [] };
}

function getNodeByPath(root, path) {
    if (path === "root") return root;
    if (!path || typeof path !== "string") return null;
    const parts = path.split(".").slice(1);
    let node = root;
    for (const p of parts) {
        if (!node) return null;
        if (p === "arg") {
            node = node.arg;
            continue;
        }
        const idx = parseInt(p, 10);
        if (Number.isNaN(idx)) return null;
        if (!Array.isArray(node.args)) return null;
        node = node.args[idx];
    }
    return node;
}

function getParentByPath(root, path) {
    if (path === "root") return null;
    if (!path || typeof path !== "string") return null;
    const parts = path.split(".");
    parts.pop();
    const parentPath = parts.join(".");
    return getNodeByPath(root, parentPath);
}

function indexFromPath(path) {
    const parts = path.split(".");
    return parseInt(parts[parts.length - 1], 10);
}

// ---------- UI render ----------
function initRuleBuilder(rootEl) {
    const conditionsUrl = rootEl.getAttribute("data-conditions-url");
    const ruleableType = rootEl.getAttribute("data-ruleable-type");
    const ruleableId = rootEl.getAttribute("data-ruleable-id");
    const ruleInput = rootEl.querySelector(".rule-builder__rule-input");
    const treeEl = rootEl.querySelector(".rule-builder__tree");

    if (!conditionsUrl || !ruleInput || !treeEl) return;

    let conditions = [];
    let rootRule = { op: "AND", args: [] };

    function readRuleFromInput() {
        let obj = {};
        try { obj = JSON.parse(ruleInput.value || "{}"); } catch (e) { obj = {}; }
        rootRule = ensureRoot(obj);
    }

    function writeRuleToInput() {
        ruleInput.value = JSON.stringify(rootRule);
    }

    function conditionSchema(op) {
        const c = findCondition(conditions, op);
        return (c && c.params_schema) || {};
    }

    function render() {
        treeEl.innerHTML = "";
        const html = renderGroup(rootRule, "root");
        treeEl.insertAdjacentHTML("beforeend", html);
        writeRuleToInput();
    }

    function renderGroup(group, path) {
        const op = group.op || "AND";
        const children = Array.isArray(group.args) ? group.args : [];

        const header =
            `<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;">
        <strong>Група</strong>
        <label style="display:flex;gap:6px;align-items:center;">
          <span>Виконати:</span>
          <select class="rb-group-op" style="display: block" data-path="${escapeHtml(path)}">
            <option value="AND" ${op === "AND" ? "selected" : ""}>Усі умови (AND)</option>
            <option value="OR"  ${op === "OR" ? "selected" : ""}>Будь-яку умову (OR)</option>
          </select>
        </label>

        <button type="button" class="btn btn-small rb-add-cond" data-path="${escapeHtml(path)}">+ Додати умову</button>
        <button type="button" class="btn btn-small rb-add-group" data-path="${escapeHtml(path)}">+ Додати групу</button>
        <button type="button" class="btn btn-small rb-add-not"  data-path="${escapeHtml(path)}">+ Додати NOT</button>

        ${(path !== "root" && !path.endsWith(".arg")) ? `<button type="button" class="btn btn-small red rb-remove-node" data-path="${escapeHtml(path)}">Видалити групу</button>` : ""}
      </div>`;

        const listItems = children.map((child, idx) => {
            const childPath = `${path}.${idx}`;
            if (isGroup(child)) {
                return `<li style="margin-top:10px;">${renderGroup(child, childPath)}</li>`;
            }
            if (isNot(child)) {
                return `<li style="margin-top:10px;">${renderNot(child, childPath)}</li>`;
            }
            return `<li style="margin-top:10px;">${renderCondition(child, childPath)}</li>`;
        }).join("");

        return `
      <div class="rb-group" style="border:1px solid #ddd;padding:12px;border-radius:10px;margin:10px 0;">
        ${header}
        <ul style="margin:10px 0 0 18px;">
          ${listItems || '<li style="opacity:.7;margin-top:8px;">Додай умову або групу</li>'}
        </ul>
      </div>
    `;
    }

    function renderCondition(cond, path) {
        const op = cond.op || (conditions[0] ? conditions[0].key : "");
        const schema = conditionSchema(op);

        // options
        const optionsHtml = conditions.map((c) => {
            const selected = c.key === op ? "selected" : "";
            return `<option value="${escapeHtml(c.key)}" ${selected}>${escapeHtml(c.title || c.key)}</option>`;
        }).join("");

        // params
        const paramsHtml = Object.keys(schema).map((k) => {
            const s = schema[k] || {};
            const v = (typeof cond[k] !== "undefined") ? cond[k] : (s.type === "array" ? [] : "");
            const multiple = s.type === "array" ? "multiple" : ""
            if (Array.isArray(s.options)) {
                const allValues = Array.isArray(v) ? v : [];
                return `
                    <label style="display:block;margin-top:6px;">
                        ${escapeHtml(k)}
                        <select
                            class="rb-param show_element ${multiple === "multiple" ? 'correct-height' : ''}" 
                            data-path="${escapeHtml(path)}"
                            data-param="${escapeHtml(k)}"
                            ${multiple}
                            style="min-width:180px;min-height:50px;display:block;margin-top:3px;"
                            >
                            ${s.options.map(opt =>
                                Array.isArray(opt)
                                    ? `<option value="${escapeHtml(opt[0])}" ${v.includes(opt[0]) ? "selected" : ""} ${opt[2] ? "disabled" : ""}>${escapeHtml(opt[1])}</option>`
                                    : `<option value="${escapeHtml(opt)}" ${v.includes(opt) ? "selected" : ""}>${escapeHtml(opt)}</option>`
                            ).join("")}
                        </select>
                    </label>
                `;
            }
            const type = s.type === "integer" ? "number" : "text";
            const minAttr = (s.type === "integer" && typeof s.min !== "undefined") ? `min="${s.min}"` : "";
            const required = s.required ? "required" : "";
            return `
                <label style="display:block;margin-top:6px;">
                  ${escapeHtml(k)}
                  <input
                    class="rb-param"
                    data-path="${escapeHtml(path)}"
                    data-param="${escapeHtml(k)}"
                    type="${type}"
                    ${minAttr}
                    ${required}
                    value="${escapeHtml(String(v))}"
                  />
                </label>
              `;
        }).join("");

        return `
        <div class="rb-cond" style="border:1px solid #ccc;padding:10px;border-radius:10px;">
          <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;">
            <select class="rb-cond-op show_element" data-path="${escapeHtml(path)}" style="min-width:180px;">
              ${optionsHtml}
            </select>
            <button type="button" class="btn btn-small red rb-remove-node" data-path="${escapeHtml(path)}">Видалити</button>
          </div>
          <div style="display:flex;gap:10px;flex-wrap:wrap;margin-top:8px;">
            ${paramsHtml}
          </div>
        </div>
      `;
    }

    function renderNot(node, path) {
        const inner = node.arg || {};
        const innerPath = path + ".arg";
        let innerHtml = "";
        if (isGroup(inner)) innerHtml = renderGroup(inner, innerPath);
        else if (isNot(inner)) innerHtml = renderNot(inner, innerPath);
        else innerHtml = renderCondition(inner, innerPath);

        return `
    <div class="rb-not" style="border:1px solid #999;padding:10px;border-radius:10px;background:rgba(0,0,0,0.03);">
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
        <strong>НЕ (NOT)</strong>

        <button type="button" class="btn btn-small rb-not-wrap-cond" data-path="${escapeHtml(path)}">
          Всередині: умова
        </button>
        <button type="button" class="btn btn-small rb-not-wrap-group" data-path="${escapeHtml(path)}">
          Всередині: група
        </button>

        <button type="button" class="btn btn-small red rb-remove-node" data-path="${escapeHtml(path)}">
          Видалити
        </button>
      </div>

      <div style="margin-top:10px;">
        ${innerHtml}
      </div>
    </div>
  `;
    }

    function addCondition(groupPath) {
        const group = getNodeByPath(rootRule, groupPath);
        if (!group.args) group.args = [];

        const firstKey = conditions[0] ? conditions[0].key : "has_payment";
        group.args.push({ op: firstKey });
    }

    function addGroup(groupPath) {
        const group = getNodeByPath(rootRule, groupPath);
        if (!group.args) group.args = [];
        group.args.push({ op: "AND", args: [] });
    }

    function addNot(groupPath) {
        const group = getNodeByPath(rootRule, groupPath);
        if (!group.args) group.args = [];

        const firstKey = conditions[0] ? conditions[0].key : "has_payment";
        group.args.push({ op: "NOT", arg: { op: firstKey } });
    }

    function wrapNotInnerAsCondition(notPath) {
        const notNode = getNodeByPath(rootRule, notPath);
        const firstKey = conditions[0] ? conditions[0].key : "has_payment";
        notNode.arg = { op: firstKey };
    }

    function wrapNotInnerAsGroup(notPath) {
        const notNode = getNodeByPath(rootRule, notPath);
        notNode.arg = { op: "AND", args: [] };
    }

    function removeNode(path) {
        if (path === "root") return;
        const parent = getParentByPath(rootRule, path);
        if (!parent) return;
        const last = path.split(".").pop();

        // якщо видаляємо елемент з args: root.0.arg.2 => last="2"
        // якщо колись захочемо видаляти arg (ми цього не робимо) => last="arg"
        if (last === "arg") return;
        const idx = parseInt(last, 10);
        if (Number.isNaN(idx)) return;
        if (!Array.isArray(parent.args)) return;
        parent.args.splice(idx, 1);
    }

    function setGroupOp(path, op) {
        const group = getNodeByPath(rootRule, path);
        group.op = op === "OR" ? "OR" : "AND";
    }

    function setConditionOp(path, op) {
        const cond = getNodeByPath(rootRule, path);
        cond.op = op;

        // reset known params for new condition
        const schema = conditionSchema(op);
        // wipe all keys except op
        Object.keys(cond).forEach((k) => { if (k !== "op") delete cond[k]; });
        // set empty params (optional)
        Object.keys(schema).forEach((k) => {
            // if options is array without multiselect (type = string) - set first value from options to cond
            cond[k] = cond[k] || ((schema[k].type === 'string') && Array.isArray(schema[k].options[0])) ? schema[k].options[0][0] : ""
        });
    }

    function setConditionParam(path, key, rawValue) {
        const cond = getNodeByPath(rootRule, path);
        const schema = conditionSchema(cond.op);
        const rules = schema[key] || {};
        cond[key] = castValue(rawValue, rules.type);
    }

    // -------- Events (delegation) --------
    treeEl.addEventListener("click", function (e) {
        const t = e.target;

        if (t.classList.contains("rb-add-cond")) {
            addCondition(t.dataset.path);
            render();
            return;
        }

        if (t.classList.contains("rb-add-group")) {
            addGroup(t.dataset.path);
            render();
            return;
        }

        if (t.classList.contains("rb-remove-node")) {
            removeNode(t.dataset.path);
            render();
            return;
        }

        if (t.classList.contains("rb-add-not")) {
            addNot(t.dataset.path);
            render();
            return;
        }

        if (t.classList.contains("rb-not-wrap-cond")) {
            wrapNotInnerAsCondition(t.dataset.path);
            render();
            return;
        }

        if (t.classList.contains("rb-not-wrap-group")) {
            wrapNotInnerAsGroup(t.dataset.path);
            render();
            return;
        }
    });

    treeEl.addEventListener("change", function (e) {
        const t = e.target;

        if (t.classList.contains("rb-group-op")) {
            setGroupOp(t.dataset.path, t.value);
            render();
            return;
        }

        if (t.classList.contains("rb-cond-op")) {
            setConditionOp(t.dataset.path, t.value);
            render();
            return;
        }
    });

    treeEl.addEventListener("input", function (e) {
        const t = e.target;
        if (t.classList.contains("rb-param")) {
            if (t.type === "select-multiple") {
                const selected = Array.from(t.selectedOptions).map(opt => opt.value);
                setConditionParam(t.dataset.path, t.dataset.param, selected);
            } else {
                setConditionParam(t.dataset.path, t.dataset.param, t.value);
            }
            writeRuleToInput();
        }
    });

    // -------- Boot --------
    let urlWithParams = conditionsUrl;
    if (ruleableType || ruleableId) {
        const usp = new URLSearchParams();
        if(ruleableType) usp.append('ruleable_type', ruleableType);
        if(ruleableId) usp.append('ruleable_id', ruleableId);
        urlWithParams += (conditionsUrl.includes('?') ? '&' : '?') + usp.toString();
    }
    fetch(urlWithParams, { headers: { Accept: "application/json" } })
        .then((r) => r.json())
        .then((data) => {
            conditions = data.conditions || [];
            readRuleFromInput();
            render();
        })
        .catch(() => {
            treeEl.innerHTML = "<div style='color:red'>Не вдалося завантажити умови</div>";
        });
}

export default function bootRuleBuilder() {
    document.querySelectorAll(".rule-builder[data-conditions-url]").forEach(initRuleBuilder);
}
